import jsQR from 'jsqr';
import { isString } from 'lodash';
import QRCode from 'qrcode';
import { createCanvas, exportSize, loadSvgAsync } from '@/utils/file-kit';
import { getLayerProps } from '@/utils/layer-kit';
import { LayerType, StretchOptionType, type IQrCodeInfo, type IQrCodeLayerProps } from '@/version-types';

export function getDrawID(id: string) {
    return `qr-${id}`;
}

/**
 * 获取二维码内容
 *
 * @param args - 参数
 */
export async function parseQRCodeToText(args: string | HTMLImageElement | HTMLCanvasElement) {
    let destImg: HTMLImageElement | HTMLCanvasElement | null = null;
    if (isString(args)) {
        destImg = await loadSvgAsync(args);
    } else {
        destImg = args;
    }
    const { width, height } = destImg;
    const { ctx, canvas } = createCanvas(width, height, destImg);
    const code = jsQR(ctx.getImageData(0, 0, width, height).data, width, height);
    const resCode = code?.data;

    if (!resCode) {
        // 添加图像预处理
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;

        // 增加对比度和亮度
        for (let i = 0; i < data.length; i += 4) {
            // 转换为灰度
            const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;

            // 提高对比度的阈值
            const threshold = 127;
            const newValue = avg > threshold ? 255 : 0;

            // 设置RGB为相同值（黑白效果）
            data[i] = newValue; // R
            data[i + 1] = newValue; // G
            data[i + 2] = newValue; // B
        }

        ctx.putImageData(imageData, 0, 0);

        // 尝试多次识别，每次缩放不同
        const scales = [1, 0.8, 1.2, 0.5, 1.5, 2];

        for (const scale of scales) {
            const scaledWidth = Math.floor(width * scale);
            const scaledHeight = Math.floor(height * scale);
            const { ctx: scaledCtx } = createCanvas(scaledWidth, scaledHeight);

            scaledCtx.drawImage(canvas, 0, 0, scaledWidth, scaledHeight);
            const code = jsQR(scaledCtx.getImageData(0, 0, scaledWidth, scaledHeight).data, scaledWidth, scaledHeight);

            if (code?.data) {
                // console.log('>>>>> code', scale);

                return code.data;
            }
        }
    }

    return resCode;
}

/**
 * 转换成二维码信息
 *
 * @param text - 文本内容
 * @param margin - 可选；外边距；默认值为 `1`
 */
export async function convertToQRCodeInfo(text: string, margin = 1) {
    const svgString = await QRCode.toString(text, {
        margin,
    });
    // console.log(svgString);
    const regex = /d="([^"]+)"/gi;
    const match = svgString.match(regex);
    // console.log(match);
    // console.log((match?.[1] || 'd=""').match(/"([^"]+)"/gi));
    const res = regex.exec(match?.[1] || 'd=""')?.[1] || '';
    // console.log(res);
    const { width } = exportSize(svgString);
    return {
        path: res,
        size: width,
    };
}

/**
 * 获取空图层数据
 */
export function getEmptyQrCodeLayerProps() {
    return getLayerProps({
        layerType: LayerType.QR_CODE,
        data: {
            text: '',
            official: '',
            fill: '',
            stroke: '',
            strokeGradient: null,
            stretchOption: StretchOptionType.fill,
            imageTransform: {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            },
        },
    });
}

/**
 * 获取矢量图层数据
 *
 * @param params - 参数
 * @param info - 二维码信息
 */
export function getQrCodeLayerProps(
    {
        width,
        height,
    }: {
        width: number;
        height: number;
    },
    info: IQrCodeInfo,
) {
    const layer = getEmptyQrCodeLayerProps();

    layer.width = width;
    layer.height = height;
    layer.data = { ...(layer.data as IQrCodeLayerProps), ...info };

    return layer;
}

/**
 * 识别文件里的文本
 *
 * @param file - 文件数据对象
 */
export function ocrImageFileToText(file: Blob): Promise<string> {
    return new Promise((resolve) => {
        if (!file) {
            resolve('');
            return;
        }
        const reader = new FileReader();
        reader.onload = async function (e: any) {
            const base64Image = e.target.result;
            const text = await parseQRCodeToText(base64Image);
            resolve(text || '');
        };
        reader.readAsDataURL(file);
    });
}
